<template>
    <div class="retell-chat-test">
        <!-- Dynamic Variables Popover -->
        <div class="chat-header">
            <a-button type="default" class="variables-button" @click="toggleVariablePopover" :disabled="isChatActive">
                <i class="material-icons">data_object</i>
                <span>Değişkenler</span>
            </a-button>

            <a-button
                v-if="!isChatActive"
                type="primary"
                class="start-button"
                @click="startChat"
            >
                <i class="material-icons">play_arrow</i>
                Chat Başlat
            </a-button>

            <a-button v-else type="danger" class="end-button" @click="endChat">
                <i class="material-icons">stop</i>
                Chat Sonlandır
            </a-button>
        </div>

        <a-popover
            v-model="variablePopoverVisible"
            title="Özel Değişken Değerleri"
            trigger="click"
            placement="bottomLeft"
            overlayClassName="variable-values-popover"
        >
            <template slot="content">
                <div v-if="extractedVariables.length === 0" class="no-variables">
                    Promptta herhangi bir değişken bulunamadı. Değişkenler &#123;&#123;değişken_adı&#125;&#125; formatında
                    olmalıdır.
                </div>
                <div v-else class="variable-values-form">
                    <div v-for="(variable, index) in extractedVariables" :key="index" class="variable-row">
                        <span class="variable-name">{{ variable.name }}:</span>
                        <a-input v-model="variable.value" placeholder="Değer girin" :disabled="isChatActive" />
                    </div>
                    <div class="form-actions">
                        <a-button type="primary" @click="saveVariableValues" :disabled="isChatActive">Kaydet</a-button>
                    </div>
                </div>
            </template>
        </a-popover>

        <!-- Chat Status -->
        <div class="chat-status" v-if="chatStatus">
            <span :class="{ 'status-active': isChatActive, 'status-ended': chatStatus === 'ended' }">
                {{ chatStatusText }}
            </span>
        </div>

        <!-- Messages Container -->
        <div class="messages-container" ref="messagesContainer">
            <div v-if="messages.length === 0 && !isChatActive" class="empty-chat">
                <i class="material-icons">chat_bubble_outline</i>
                <p>Chat başlatarak LLM'i test edin</p>
            </div>

            <div
                v-for="(message, index) in messages"
                :key="index"
                class="message-item"
                :class="getMessageClass(message)"
            >
                <!-- Knowledge Base Retrieval -->
                <template v-if="message.role === 'knowledge_base_retrieval'">
                    <div class="kb-retrieval-item" @click="showKnowledgeBaseModal(message._knowledgeBaseContent)">
                        <i class="material-icons">search</i>
                        <span>Knowledge Base Retrieval</span>
                    </div>
                </template>

                <!-- Normal user/agent messages -->
                <template v-else-if="message.role === 'agent' || message.role === 'user'">
                    <div class="message-header">
                        <span class="role-label">{{ message.role === "agent" ? "Agent" : "Kullanıcı" }}</span>
                        <span class="timestamp">{{ formatTimestamp(message.created_timestamp) }}</span>
                    </div>
                    <div class="message-content">{{ message.content }}</div>
                    <a-button
                        v-if="message.role === 'user'"
                        type="link"
                        size="small"
                        class="delete-message-btn"
                        @click="deleteMessage(index)"
                        :disabled="isWaitingResponse"
                    >
                        <i class="material-icons">delete</i>
                    </a-button>
                </template>

                <!-- Tool call invocation - Accordion -->
                <template v-else-if="message.role === 'tool_call_invocation'">
                    <a-collapse class="tool-collapse">
                        <a-collapse-panel>
                            <template slot="header">
                                <div class="tool-header">
                                    <i class="material-icons">build</i>
                                    <span>Tool Invocation: {{ message.name }}</span>
                                </div>
                            </template>
                            <div class="tool-details">
                                <div class="tool-row">
                                    <span class="tool-label">tool_call_id:</span>
                                    <code>{{ message.tool_call_id }}</code>
                                </div>
                                <div class="tool-row">
                                    <span class="tool-label">Argümanlar:</span>
                                    <pre class="tool-args-pre">{{ formatToolArguments(message.arguments) }}</pre>
                                </div>
                            </div>
                        </a-collapse-panel>
                    </a-collapse>
                </template>

                <!-- Tool call result - Accordion -->
                <template v-else-if="message.role === 'tool_call_result'">
                    <a-collapse class="tool-collapse tool-result-collapse">
                        <a-collapse-panel>
                            <template slot="header">
                                <div class="tool-header tool-result-header">
                                    <i class="material-icons">check_circle</i>
                                    <span>Tool Result</span>
                                </div>
                            </template>
                            <div class="tool-details">
                                <div class="tool-row">
                                    <span class="tool-label">tool_call_id:</span>
                                    <code>{{ message.tool_call_id }}</code>
                                </div>
                                <div class="tool-row">
                                    <span class="tool-label">Sonuç:</span>
                                    <pre class="tool-result-pre">{{ formatToolResult(message.content) }}</pre>
                                </div>
                            </div>
                        </a-collapse-panel>
                    </a-collapse>
                </template>

                <!-- Node transition -->
                <template v-else-if="message.role === 'node_transition'">
                    <div class="message-header">
                        <span class="role-label"><i class="material-icons">swap_horiz</i> Node Geçişi</span>
                        <span class="timestamp">{{ formatTimestamp(message.created_timestamp) }}</span>
                    </div>
                    <div class="message-content transition-content">
                        <span class="from-state">{{ message.former_node_name || message.former_node_id || "-" }}</span>
                        <i class="material-icons">arrow_forward</i>
                        <span class="to-state">{{ message.new_node_name || message.new_node_id || "-" }}</span>
                    </div>
                </template>

                <!-- State transition -->
                <template v-else-if="message.role === 'state_transition'">
                    <div class="message-header">
                        <span class="role-label"><i class="material-icons">swap_vert</i> State Geçişi</span>
                        <span class="timestamp">{{ formatTimestamp(message.created_timestamp) }}</span>
                    </div>
                    <div class="message-content transition-content">
                        <span class="from-state">{{ message.former_state_name || "-" }}</span>
                        <i class="material-icons">arrow_forward</i>
                        <span class="to-state">{{ message.new_state_name || "-" }}</span>
                    </div>
                </template>
            </div>

            <!-- Typing indicator -->
            <div v-if="isWaitingResponse" class="message-item agent typing-indicator">
                <div class="typing-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        </div>

        <!-- Message Input -->
        <div class="message-input-container" v-if="isChatActive">
            <a-button
                type="default"
                class="reset-btn"
                @click="resetChat"
                :disabled="isWaitingResponse || messages.length === 0"
                title="Sıfırla"
            >
                <i class="material-icons">refresh</i>
            </a-button>
            <a-input
                v-model="messageInput"
                placeholder="Mesajınızı yazın..."
                @pressEnter="sendMessage"
                :disabled="isWaitingResponse"
                ref="messageInput"
            />
            <a-button
                type="primary"
                @click="sendMessage"
                :disabled="!messageInput.trim() || isWaitingResponse"
                :loading="isWaitingResponse"
            >
                <i class="material-icons">send</i>
            </a-button>
        </div>

        <!-- Knowledge Base Modal -->
        <a-modal
            v-model="knowledgeBaseModalVisible"
            title="Knowledge Base Retrieval"
            :footer="null"
            width="700px"
            class="kb-modal"
        >
            <div v-if="currentKnowledgeBaseContent && currentKnowledgeBaseContent.length > 0" class="kb-content-list">
                <div
                    v-for="(chunk, chunkIndex) in currentKnowledgeBaseContent"
                    :key="chunkIndex"
                    class="kb-chunk"
                >
                    <div class="kb-chunk-header">Chunk {{ chunkIndex + 1 }}</div>
                    <div class="kb-chunk-content">{{ chunk }}</div>
                    <a-button
                        type="link"
                        size="small"
                        class="expand-btn"
                        @click="toggleChunkExpand(chunkIndex)"
                    >
                        {{ expandedChunks[chunkIndex] ? 'Daralt' : 'Genişlet' }}
                    </a-button>
                </div>
            </div>
            <div v-else class="kb-empty">
                Knowledge base içeriği bulunamadı.
            </div>
        </a-modal>

        <!-- Logs -->
        <div class="test-logs" v-if="!isChatActive && logs.length > 0">
            <h4>Log</h4>
            <div class="log-entries">
                <div v-for="(logEntry, index) in logs" :key="index" class="log-entry">
                    {{ logEntry }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";
import log2 from "../../../../../../../utils/log2";
import { VOICE_AGENT_API } from "../../../../../../../api/voiceAgent";

var logFn = log2("[RetellChatTest]");

export default {
    name: "RetellChatTest",
    props: {
        agent: {
            type: Object,
            required: true,
        },
    },
    data: function() {
        return {
            isChatActive: false,
            isWaitingResponse: false,
            chatStatus: "",
            messages: [],
            transcript: [],
            messageInput: "",
            logs: [],
            variablePopoverVisible: false,
            extractedVariables: [],
            dynamicVariables: {},
            knowledgeBaseModalVisible: false,
            currentKnowledgeBaseContent: [],
            expandedChunks: {},
            lastKnowledgeBaseContents: [], // Önceki KB içeriklerini takip et
        };
    },
    computed: Object.assign({}, mapGetters({
        channels: "channel/channels",
    }), {
        chatStatusText: function() {
            switch (this.chatStatus) {
                case "ongoing":
                    return "Chat aktif";
                case "ended":
                    return "Chat sonlandırıldı";
                case "error":
                    return "Hata oluştu";
                default:
                    return this.chatStatus;
            }
        },
    }),
    watch: {
        "agent.llm.general_prompt": {
            handler: function(newValue) {
                if (newValue) {
                    this.extractVariablesFromPrompt(newValue);
                }
            },
            immediate: true,
        },
    },
    mounted: function() {
        if (this.agent && this.agent.llm && this.agent.llm.general_prompt) {
            this.extractVariablesFromPrompt(this.agent.llm.general_prompt);
        }
    },
    methods: {
        toggleVariablePopover: function() {
            this.variablePopoverVisible = !this.variablePopoverVisible;
        },

        extractVariablesFromPrompt: function(promptText) {
            if (!promptText) return;

            var systemVariables = [
                "current_agent_state",
                "previous_agent_state",
                "current_time",
                "current_time_Europe/Istanbul",
                "call_type",
                "direction",
                "user_number",
                "agent_number",
            ];

            var regex = /\{\{([^}]+)\}\}/g;
            var matches = promptText.matchAll(regex);
            var matchesArray = Array.from(matches);

            var variables = [];
            var variableSet = new Set();
            var self = this;

            matchesArray.forEach(function(match) {
                var varName = match[1].trim();

                if (systemVariables.indexOf(varName) === -1 && !variableSet.has(varName)) {
                    variableSet.add(varName);
                    variables.push({
                        name: varName,
                        value: self.dynamicVariables[varName] || "",
                    });
                }
            });

            this.extractedVariables = variables;
        },

        saveVariableValues: function() {
            this.dynamicVariables = {};
            var self = this;
            this.extractedVariables.forEach(function(variable) {
                if (variable.value) {
                    self.dynamicVariables[variable.name] = variable.value;
                }
            });

            this.addLog("Değişken değerleri kaydedildi: " + Object.keys(this.dynamicVariables).length + " değişken");
            this.variablePopoverVisible = false;
        },

        addLog: function(message) {
            this.logs.unshift(new Date().toLocaleTimeString() + ": " + message);
            if (this.logs.length > 20) {
                this.logs.pop();
            }
        },

        startChat: function() {
            if (!this.agent || !this.agent.agent_id) {
                this.addLog("Agent ID bulunamadı!");
                return;
            }

            this.transcript = [];
            this.messages = [];
            this.lastKnowledgeBaseContents = [];
            this.isChatActive = true;
            this.chatStatus = "ongoing";
            this.addLog("Chat başlatıldı - ilk mesaj alınıyor...");
            this.$emit("chatStarted", { agentId: this.agent.agent_id });

            // Boş transcript ile ilk request'i at (begin_message için)
            this.callPlaygroundCompletion();

            var self = this;
            this.$nextTick(function() {
                if (self.$refs.messageInput) {
                    self.$refs.messageInput.focus();
                }
            });
        },

        endChat: function() {
            this.isChatActive = false;
            this.chatStatus = "ended";
            this.addLog("Chat sonlandırıldı");
            this.$emit("chatEnded", { agentId: this.agent.agent_id });
        },

        resetChat: function() {
            this.transcript = [];
            this.messages = [];
            this.lastKnowledgeBaseContents = [];
            this.addLog("Chat sıfırlandı - transcript temizlendi");
        },

        deleteMessage: function(index) {
            if (index >= 0 && index < this.messages.length) {
                var deletedMessage = this.messages[index];
                
                // Transcript'ten de sil (message_id veya content bazlı)
                var newTranscript = [];
                this.transcript.forEach(function(t) {
                    var isSame = (t.message_id && t.message_id === deletedMessage.message_id) ||
                                 (t.content === deletedMessage.content && t.role === deletedMessage.role);
                    if (!isSame) {
                        newTranscript.push(t);
                    }
                });
                this.transcript = newTranscript;
                
                // Messages'tan sil
                this.messages.splice(index, 1);
                
                // Eğer KB retrieval mesajı önünde varsa, onu da sil
                if (index > 0 && this.messages[index - 1] && this.messages[index - 1].role === "knowledge_base_retrieval") {
                    this.messages.splice(index - 1, 1);
                }
                
                this.addLog("Mesaj silindi");
            }
        },

        sendMessage: function() {
            if (!this.messageInput.trim() || this.isWaitingResponse) {
                return;
            }

            var userMessage = this.messageInput.trim();
            this.messageInput = "";

            // Kullanıcı mesajını hemen UI'da göster (optimistic update)
            this.messages.push({
                message_id: "temp_user_" + Date.now(),
                role: "user",
                content: userMessage,
                created_timestamp: Date.now(),
                _isTemp: true, // Geçici mesaj flag'i
            });

            // Transcript'e de ekle (API request için)
            this.transcript.push({
                content: userMessage,
                role: "user",
            });

            this.scrollToBottom();
            this.callPlaygroundCompletion();
        },

        callPlaygroundCompletion: function() {
            var self = this;
            this.isWaitingResponse = true;

            var dynamicVars = {
                call_id: "playground",
                session_type: "voice",
                call_type: "web_call",
            };

            var keys = Object.keys(this.dynamicVariables);
            for (var i = 0; i < keys.length; i++) {
                dynamicVars[keys[i]] = this.dynamicVariables[keys[i]];
            }

            VOICE_AGENT_API.chats.playgroundCompletion(
                this.agent.agent_id,
                this.transcript,
                dynamicVars,
                [],
                "",
                false
            ).then(function(response) {
                logFn("playgroundCompletion response", response);

                // Response'da transcript var, messages değil
                var responseTranscript = response.transcript || response.messages || [];
                
                if (responseTranscript && Array.isArray(responseTranscript)) {
                    var newKnowledgeBaseContents = response.knowledge_base_retrieved_contents || [];
                    
                    // KB içeriği değişti mi kontrol et
                    var kbChanged = false;
                    var kbContentToAttach = null;
                    
                    if (newKnowledgeBaseContents.length > 0) {
                        var oldKbStr = JSON.stringify(self.lastKnowledgeBaseContents);
                        var newKbStr = JSON.stringify(newKnowledgeBaseContents);
                        
                        if (oldKbStr !== newKbStr) {
                            kbChanged = true;
                            kbContentToAttach = newKnowledgeBaseContents.slice();
                            logFn("KB içeriği değişti, yeni agent mesajına eklenecek");
                        }
                    }
                    
                    // KB state'ini güncelle
                    self.lastKnowledgeBaseContents = newKnowledgeBaseContents.slice();
                    
                    // Mevcut KB retrieval mesajlarını sakla (message_id -> content map)
                    var existingKbMessages = {};
                    self.messages.forEach(function(m) {
                        if (m.role === "knowledge_base_retrieval") {
                            existingKbMessages[m.message_id] = m;
                        }
                    });
                    
                    // Geçici kullanıcı mesajlarını sakla (henüz response'da olmayan)
                    var tempUserMessages = [];
                    self.messages.forEach(function(m) {
                        if (m._isTemp && m.role === "user") {
                            tempUserMessages.push(m);
                        }
                    });
                    
                    // Messages'ı yeniden oluştur - response transcript + KB retrieval mesajları
                    var newMessages = [];
                    
                    // KB değiştiyse, son agent mesajını bul
                    var lastAgentIndex = -1;
                    if (kbChanged && kbContentToAttach) {
                        for (var i = responseTranscript.length - 1; i >= 0; i--) {
                            if (responseTranscript[i].role === "agent") {
                                lastAgentIndex = i;
                                break;
                            }
                        }
                    }
                    
                    // Response'daki kullanıcı mesajlarının content'lerini topla
                    var responseUserContents = {};
                    responseTranscript.forEach(function(msg) {
                        if (msg.role === "user") {
                            responseUserContents[msg.content] = true;
                        }
                    });
                    
                    // Her transcript mesajı için, önceki KB mesajını kontrol et ve koru
                    responseTranscript.forEach(function(msg, idx) {
                        // Mevcut KB retrieval mesajını bu mesajın önüne ekle (eğer varsa)
                        var kbKey = "kb_before_" + msg.message_id;
                        if (existingKbMessages[kbKey]) {
                            newMessages.push(existingKbMessages[kbKey]);
                        }
                        
                        // Yeni KB retrieval - son agent mesajından önce ekle
                        if (kbContentToAttach && idx === lastAgentIndex) {
                            var newKbMsg = {
                                message_id: "kb_before_" + msg.message_id,
                                role: "knowledge_base_retrieval",
                                _knowledgeBaseContent: kbContentToAttach,
                                created_timestamp: msg.created_timestamp - 1,
                            };
                            newMessages.push(newKbMsg);
                            kbContentToAttach = null;
                        }
                        
                        newMessages.push(Object.assign({}, msg));
                    });
                    
                    // Henüz response'da olmayan geçici kullanıcı mesajlarını sona ekle
                    tempUserMessages.forEach(function(tempMsg) {
                        if (!responseUserContents[tempMsg.content]) {
                            newMessages.push(tempMsg);
                        }
                    });

                    // Messages'ı güncelle
                    self.messages = newMessages;
                    
                    // Transcript'i güncelle (sonraki request için)
                    self.transcript = responseTranscript.slice();
                }

                self.scrollToBottom();
            }).catch(function(error) {
                logFn("playgroundCompletion error", error);
                self.addLog("Mesaj gönderilemedi: " + (error.message || "Bilinmeyen hata"));

                self.messages.push({
                    message_id: "error_" + Date.now(),
                    role: "agent",
                    content: "Hata: " + (error.message || "Mesaj gönderilemedi"),
                    created_timestamp: Date.now(),
                });
            }).finally(function() {
                self.isWaitingResponse = false;
                self.$nextTick(function() {
                    if (self.$refs.messageInput) {
                        self.$refs.messageInput.focus();
                    }
                });
            });
        },

        getMessageClass: function(message) {
            var classes = [message.role];

            if (message.role === "tool_call_invocation" || message.role === "tool_call_result") {
                classes.push("tool-message");
            }

            if (message.role === "node_transition" || message.role === "state_transition") {
                classes.push("transition-message");
            }

            if (message.role === "knowledge_base_retrieval") {
                classes.push("kb-retrieval-message");
            }

            return classes;
        },

        formatTimestamp: function(timestamp) {
            if (!timestamp) return "";
            var date = new Date(timestamp);
            return date.toLocaleTimeString();
        },

        formatToolArguments: function(args) {
            if (!args) return "-";
            try {
                if (typeof args === "string") {
                    var parsed = JSON.parse(args);
                    return JSON.stringify(parsed, null, 2);
                }
                return JSON.stringify(args, null, 2);
            } catch (e) {
                return args;
            }
        },

        formatToolResult: function(content) {
            if (!content) return "-";
            try {
                if (typeof content === "string") {
                    var parsed = JSON.parse(content);
                    return JSON.stringify(parsed, null, 2);
                }
                return JSON.stringify(content, null, 2);
            } catch (e) {
                return content;
            }
        },

        showKnowledgeBaseModal: function(content) {
            this.currentKnowledgeBaseContent = content || [];
            this.expandedChunks = {};
            this.knowledgeBaseModalVisible = true;
        },

        toggleChunkExpand: function(index) {
            this.$set(this.expandedChunks, index, !this.expandedChunks[index]);
        },

        scrollToBottom: function() {
            var self = this;
            this.$nextTick(function() {
                var container = self.$refs.messagesContainer;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            });
        },
    },
};
</script>

<style scoped lang="scss">
.retell-chat-test {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 400px;

    .chat-header {
        display: flex;
        gap: 10px;
        margin-bottom: 10px;

        .variables-button {
            display: flex;
            align-items: center;
            gap: 5px;

            .material-icons {
                font-size: 18px;
            }
        }

        .start-button,
        .end-button {
            display: flex;
            align-items: center;
            gap: 5px;
            margin-left: auto;

            .material-icons {
                font-size: 18px;
            }
        }
    }

    .chat-status {
        text-align: center;
        padding: 8px;
        margin-bottom: 10px;
        border-radius: 4px;
        background-color: #f5f5f5;
        font-weight: 500;

        .status-active {
            color: #52c41a;
        }

        .status-ended {
            color: #999;
        }
    }

    .messages-container {
        flex: 1;
        overflow-y: auto;
        padding: 15px;
        background-color: #f9f9f9;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        margin-bottom: 10px;
        min-height: 250px;
        max-height: 400px;

        .empty-chat {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 100%;
            color: #999;

            .material-icons {
                font-size: 48px;
                margin-bottom: 10px;
            }

            p {
                margin: 0;
                font-style: italic;
            }
        }

        .message-item {
            margin-bottom: 12px;
            padding: 10px 14px;
            border-radius: 8px;
            max-width: 85%;
            position: relative;

            &.user {
                background-color: #1890ff;
                color: white;
                margin-left: auto;

                .message-header {
                    .role-label {
                        color: rgba(255, 255, 255, 0.85);
                    }

                    .timestamp {
                        color: rgba(255, 255, 255, 0.65);
                    }
                }

                .delete-message-btn {
                    position: absolute;
                    right: -30px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #999;
                    padding: 0;

                    &:hover {
                        color: #ff4d4f;
                    }

                    .material-icons {
                        font-size: 16px;
                    }
                }
            }

            &.agent {
                background-color: #fff;
                border: 1px solid #e8e8e8;
                margin-right: auto;
            }

            &.tool-message {
                background-color: transparent;
                border: none;
                padding: 0;
                margin-right: auto;
                max-width: 95%;

                .tool-collapse {
                    border: 1px solid #b7eb8f;
                    border-radius: 4px;
                    overflow: hidden;

                    &.tool-result-collapse {
                        border-color: #91d5ff;
                    }
                }

                .tool-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 13px;
                    font-weight: 500;
                    color: #52c41a;

                    .material-icons {
                        font-size: 16px;
                    }
                }

                .tool-result-header {
                    color: #1890ff;
                }

                .tool-details {
                    font-size: 12px;

                    .tool-row {
                        margin-bottom: 8px;

                        &:last-child {
                            margin-bottom: 0;
                        }

                        .tool-label {
                            font-weight: 600;
                            display: block;
                            margin-bottom: 4px;
                            color: #666;
                        }

                        code {
                            background-color: rgba(0, 0, 0, 0.06);
                            padding: 2px 6px;
                            border-radius: 3px;
                            font-size: 11px;
                        }

                        .tool-args-pre,
                        .tool-result-pre {
                            background-color: #f5f5f5;
                            padding: 8px;
                            border-radius: 4px;
                            margin: 0;
                            font-size: 11px;
                            overflow-x: auto;
                            max-height: 200px;
                            white-space: pre-wrap;
                            word-break: break-all;
                        }
                    }
                }
            }

            &.transition-message {
                background-color: #fff7e6;
                border: 1px solid #ffd591;
                margin: 8px auto;
                max-width: 70%;
                text-align: center;

                .message-header .role-label {
                    color: #fa8c16;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 4px;

                    .material-icons {
                        font-size: 16px;
                    }
                }

                .transition-content {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;

                    .from-state,
                    .to-state {
                        padding: 4px 8px;
                        background-color: rgba(0, 0, 0, 0.04);
                        border-radius: 4px;
                        font-weight: 500;
                    }

                    .material-icons {
                        font-size: 18px;
                        color: #fa8c16;
                    }
                }
            }

            &.kb-retrieval-message {
                background-color: transparent;
                border: none;
                padding: 4px 0;
                margin-right: auto;
                max-width: 100%;

                .kb-retrieval-item {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 12px;
                    background-color: #f0f5ff;
                    border: 1px solid #adc6ff;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 13px;
                    color: #1890ff;
                    transition: all 0.2s;

                    &:hover {
                        background-color: #e6f0ff;
                        border-color: #85a5ff;
                    }

                    .material-icons {
                        font-size: 16px;
                    }
                }
            }

            .message-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-bottom: 4px;
                font-size: 12px;
                flex-wrap: wrap;
                gap: 4px;

                .role-label {
                    font-weight: 600;
                    color: #666;
                }

                .timestamp {
                    color: #999;
                }
            }

            .message-content {
                font-size: 14px;
                line-height: 1.5;
                white-space: pre-wrap;
                word-break: break-word;
            }

            &.typing-indicator {
                padding: 15px 20px;

                .typing-dots {
                    display: flex;
                    gap: 4px;

                    span {
                        width: 8px;
                        height: 8px;
                        background-color: #999;
                        border-radius: 50%;
                        animation: typing 1.4s infinite ease-in-out both;

                        &:nth-child(1) {
                            animation-delay: -0.32s;
                        }

                        &:nth-child(2) {
                            animation-delay: -0.16s;
                        }
                    }
                }
            }
        }
    }

    .message-input-container {
        display: flex;
        gap: 10px;

        .reset-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            
            .material-icons {
                font-size: 18px;
            }
        }

        .ant-input {
            flex: 1;
        }

        .ant-btn {
            display: flex;
            align-items: center;
            justify-content: center;

            .material-icons {
                font-size: 18px;
            }
        }
    }

    .test-logs {
        margin-top: 15px;
        padding: 10px;
        background-color: #fff;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        max-height: 150px;
        overflow-y: auto;

        h4 {
            margin: 0 0 10px 0;
            font-size: 13px;
            color: #666;
        }

        .log-entries {
            .log-entry {
                padding: 4px 0;
                border-bottom: 1px solid #f0f0f0;
                font-size: 11px;
                font-family: monospace;
                color: #666;

                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }
}

@keyframes typing {
    0%,
    80%,
    100% {
        transform: scale(0);
    }
    40% {
        transform: scale(1);
    }
}

.variable-values-popover {
    min-width: 350px;
    max-width: 500px;

    .variable-values-form {
        display: flex;
        flex-direction: column;
        gap: 10px;

        .variable-row {
            display: flex;
            align-items: center;
            gap: 10px;

            .variable-name {
                min-width: 120px;
                font-weight: 500;
            }
        }

        .form-actions {
            margin-top: 10px;
            display: flex;
            justify-content: flex-end;
        }
    }

    .no-variables {
        padding: 20px 0;
        color: #999;
        font-style: italic;
        text-align: center;
    }
}

.kb-modal {
    .kb-content-list {
        max-height: 500px;
        overflow-y: auto;

        .kb-chunk {
            margin-bottom: 16px;
            padding: 12px;
            background-color: #f9f9f9;
            border: 1px solid #e8e8e8;
            border-radius: 4px;

            &:last-child {
                margin-bottom: 0;
            }

            .kb-chunk-header {
                font-weight: 600;
                margin-bottom: 8px;
                color: #333;
            }

            .kb-chunk-content {
                font-size: 13px;
                line-height: 1.6;
                white-space: pre-wrap;
                word-break: break-word;
                max-height: 150px;
                overflow: hidden;
                transition: max-height 0.3s ease;
            }

            &.expanded .kb-chunk-content {
                max-height: none;
            }

            .expand-btn {
                margin-top: 8px;
                padding: 0;
                font-size: 12px;
            }
        }
    }

    .kb-empty {
        text-align: center;
        color: #999;
        padding: 40px;
    }
}
</style>
